import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from './usefulFuncs';

export const unassignedDocGroupName = 'Unassigned Documents';

export function isNewsArticle(docType) {
  return docType.includes('News Article');
}

export function addFileDefaults(fileOrFiles, extraParams = {}) {
  const pdfDefaults = {
    do_table_extraction: true,
    do_vision_extraction: true,
  };
  const extractionOptionTypes = ['pdf', 'doc', 'docx'];
  if (Array.isArray(fileOrFiles)) {
    return fileOrFiles.map((f) => ({
      ...f,
      ...extraParams,
      extract_formula_dependencies: true,
      ...(extractionOptionTypes.includes(f.type) ? pdfDefaults : {}),
      docType: null,
      company: null,
    }));
  } else {
    return {
      ...fileOrFiles,
      ...extraParams,
      extract_formula_dependencies: true,
      ...(extractionOptionTypes.includes(fileOrFiles.type) ? pdfDefaults : {}),
      docType: null,
      company: null,
    };
  }
}

/* ******************************************************
  UTILITIES
****************************************************** */

export const filterDocsByTags = (documents, tags) => {
  const companyTags = tags.filter((tag) => tag.doc_tag_id !== null);
  const publicTags = tags.filter((tag) => tag.company_id !== null);
  const filtered = [];

  companyTags.forEach((tag) => {
    filtered.push(...documents.filter((doc) => doc.doc_tag?.id === tag.doc_tag_id));
  });

  publicTags.forEach((tag) => {
    filtered.push(...documents.filter((doc) => doc.company_id === tag.company_id));
  });

  return filtered;
};

export const filterDocsByReportingFilter = (documents, nextFilters) =>
  documents.filter((doc) => {
    let matches = true;
    if (nextFilters.quarter) {
      matches = matches && doc.fiscal_quarter === parseInt(nextFilters.quarter, 10);
    }
    if (nextFilters.month) {
      matches = matches && doc.fiscal_month === parseInt(nextFilters.month, 10);
    }
    if (nextFilters.year) {
      matches = matches && doc.fiscal_year === parseInt(nextFilters.year, 10);
    }
    return matches;
  });

export function parseDocGroup(doc) {
  const { security_symbol, security_name, doc_tag } = doc;

  if (security_symbol) {
    const symbolAndName = security_name ? security_symbol + ' - ' + security_name : security_symbol;
    return symbolAndName;
  }

  if (doc_tag) {
    const tagName = doc_tag.name;
    return tagName;
  }

  return unassignedDocGroupName;
}

export function filterTickerDocsFromDocs(docs, ticker) {
  if (!docs) return [];
  return docs.filter((doc) => doc.security_symbol !== ticker);
}

export function filterIsRecentDocs(docs, isUserDocs) {
  if (!docs) return [];
  if (isUserDocs) {
    return docs.filter((doc) => doc.is_recent);
  }
  return docs.filter((doc) => doc.is_recent && doc.added_by_user_id === null);
}

export function assembleDocumentTypes(docs) {
  if (!docs) return [];
  const documentTypes = docs.map((d) => (d.content_type === 'text/csv' ? 'CSV' : d.doc_type));
  return [...new Set(documentTypes)].sort();
}

export function isDocSelected(d, selectedDocs) {
  if (selectedDocs === null) {
    return false;
  }

  let isSelected = false;
  selectedDocs.forEach((selectedDoc) => {
    if (selectedDoc.id === d.id) {
      isSelected = true;
    }
  });
  return isSelected;
}

export function extractDocTypes(docs) {
  if (!docs) return [];

  const longDocTypes = [];
  const shortDocTypes = [];
  const endOfList = [];
  docs.forEach((d) => {
    const { display_category, doc_type } = d;
    const lookAtType = display_category;
    if (lookAtType === 'unknown') {
      !endOfList.includes('unknown') && endOfList.push(lookAtType);
    } else {
      if (lookAtType?.length > 7) {
        !longDocTypes.includes(lookAtType) && longDocTypes.push(lookAtType);
      } else {
        !shortDocTypes.includes(lookAtType) && shortDocTypes.push(lookAtType);
      }
    }
  });
  return [...shortDocTypes.sort(), ...longDocTypes.sort(), ...endOfList];
}

export function getDocTypeDisplayValue(docType) {
  if (!docType) return 'Other Materials';

  const documentTypeNames = {
    unknown: 'Other Materials',
  };
  return documentTypeNames[docType] || docType;
}

/* ******************************************************
  API SERVICES
****************************************************** */

export async function fetchNewsDocsByFilters(filters) {
  let URL = `v1/genai/documents?category=news&tickers=${filters.tickers.join(',').toUpperCase()}`;
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data?.documents || null;
  } else {
    return null;
  }
}

export async function fetchCompanyDocsByFilters(filters) {
  let URL = `v1/genai/documents?category=company`;
  if (filters.tickers) {
    URL += `&tickers=${filters.tickers.join(',').toUpperCase()}`;
  }
  if (filters.companyIds) {
    URL += `&company_ids=${filters.companyIds.join(',')}`;
  }
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data?.documents || null;
  } else {
    return null;
  }
}

export async function fetchCompanyTagDocsByFilters(filters) {
  let URL = `v1/genai/documents?category=company&doc_tag_ids=${filters.companyTagIds.join(',')}`;
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(URL, status, data);
  handle400Statuses(status);
  if (status === 200) {
    return data?.documents || null;
  } else {
    return null;
  }
}

export async function fetchDocumentsForTags(tags) {
  const companyTagIds = tags.filter((tag) => tag.doc_tag_id !== null).map((tag) => tag.doc_tag_id);
  const publicTagIds = tags.filter((tag) => tag.company_id !== null).map((tag) => tag.company_id);

  let documents = [];

  // Helper to fetch and accumulate results
  async function fetchAndAccum(category, paramKey, ids) {
    if (!ids || !ids.length) return;
    await Promise.all(
      ids.map(async (id) => {
        const url = `v1/genai/documents?category=${category}&${paramKey}=${id}`;
        const response = await sendApiRequest('get', url);
        logNetRequest(url, response.status, response.data);
        handle400Statuses(response.status);
        if (response.status === 200 && response.data?.documents) {
          documents = documents.concat(response.data.documents);
        }
      })
    );
  }

  // Fetch by doc_tag_id from company and user categories
  await Promise.all([
    fetchAndAccum('company', 'doc_tag_ids', companyTagIds),
    fetchAndAccum('user', 'doc_tag_ids', companyTagIds),
    fetchAndAccum('company', 'company_ids', publicTagIds),
    fetchAndAccum('user', 'company_ids', publicTagIds),
  ]);

  return documents;
}

export async function fetchRecentDocumentsForTags(tags) {
  const documents = await fetchDocumentsForTags(tags);
  return documents.filter((doc) => doc.is_recent);
}
